import React, { useEffect, useState } from "react";

interface ImageComponentProps {
  imageUrl: string;
  headers?: HeadersInit;
  token?: string;
  alt?: string;
  placeholder?: string;
  fallback?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  loading?: "lazy" | "eager";
}

export const ImageWithToken: React.FC<ImageComponentProps> = React.memo(
  ({
    imageUrl,
    token,
    headers,
    alt = "Image",
    placeholder,
    fallback,
    className,
    style,
    onClick,
    loading = "eager",
  }) => {
    const [imgSrc, setImgSrc] = useState<string>(placeholder || "");
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
      let didCancel = false;
      const accessToken = token;

      const fetchImage = async () => {
        try {
          const headersObj = new Headers(headers);
          if (accessToken && !headersObj.has("Authorization")) {
            headersObj.append("Authorization", `Bearer ${accessToken}`);
          }

          const response = await fetch(imageUrl, { headers: headersObj });

          if (!response.ok) {
            throw new Error("Failed to fetch image");
          }
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          if (!didCancel) {
            setImgSrc(url);
          }
        } catch (error) {
          if (!didCancel) {
            !__ISPROD__ && console.error("Error fetching image:", error);
            setError(true);
          }
        }
      };

      fetchImage();

      return () => {
        didCancel = true;
        if (imgSrc && imgSrc !== placeholder && imgSrc !== fallback) {
          URL.revokeObjectURL(imgSrc);
        }
      };
    }, [imageUrl, token]);

    return (
      <img
        src={error && fallback ? fallback : imgSrc}
        alt={alt}
        className={className}
        style={style}
        onError={() => setError(true)}
        onClick={onClick}
        loading={loading}
      />
    );
  },
);
